import axios, { AxiosError } from 'axios';


const BRANDFETCH_CLIENT_ID = process.env.REACT_APP_BRANDFETCH_CLIENT_ID;
const BRANDFETCH_API_BASE_URL = 'https://api.brandfetch.io/v2';

interface BrandSearchResult {
  icon: string | null;
  name: string | null;
  domain: string;
  claimed: boolean;
  brandId: string;
}

interface BrandfetchApiError {
  message?: string;
}

/**
 * Generates a Brandfetch Logo Link URL for direct embedding.
 * Requires a Client ID (VITE_BRANDFETCH_CLIENT_ID) to be set in environment variables.
 * @param domain The company domain (e.g., 'google.com').
 * @param options Optional parameters for customization (type, theme, size).
 * @returns The Logo Link URL string, or null if Client ID is missing.
 */
export const generateLogoLinkUrl = (
  domain: string,
  options?: {
    type?: 'logo' | 'icon' | 'symbol';
    theme?: 'dark' | 'light';
    fallback?: 'brandfetch' | 'transparent' | 'lettermark' | '404';
    width?: number;
    height?: number;
  }
): string | null => {
  if (!BRANDFETCH_CLIENT_ID) {
    console.error('[brandfetch.ts] Missing VITE_BRANDFETCH_CLIENT_ID environment variable for Logo Link.');
    return null;
  }

  let path = `https://cdn.brandfetch.io/${domain}`;

  let actualFallback = options?.fallback;
  if (!actualFallback && options?.type === 'icon') {
    actualFallback = 'lettermark';
  }

  if (options?.type) {
    path += `/${options.type}`;
  }
  if (options?.theme) {
    path += `/theme/${options.theme}`;
  }
  if (actualFallback) {
    path += `/fallback/${actualFallback}`;
  }
  if (options?.height) {
    path += `/h/${options.height}`;
  }
  if (options?.width) {
    path += `/w/${options.width}`;
  }

  const url = `${path}?c=${BRANDFETCH_CLIENT_ID}`;

  return url;
};


/**
 * Searches for brands by name using the Brandfetch Search API.
 * Requires a Client ID (VITE_BRANDFETCH_CLIENT_ID) to be set in environment variables.
 * @param query The brand name or query to search for.
 * @returns A promise resolving to an array of BrandSearchResult objects, or null if an error occurs.
 */
export const searchBrandsByName = async (query: string): Promise<BrandSearchResult[] | null> => {
  // NOTE: This is an external API call to Brandfetch, so we don't use our apiClient with JWT
  if (!BRANDFETCH_CLIENT_ID) {
    console.error('[brandfetch.ts] Missing VITE_BRANDFETCH_CLIENT_ID environment variable for Brand Search API.');
    return null;
  }

  if (!query || query.trim().length === 0) {
    return [];
  }

  const encodedQuery = encodeURIComponent(query);
  const url = `${BRANDFETCH_API_BASE_URL}/search/${encodedQuery}?c=${BRANDFETCH_CLIENT_ID}`;

  console.log(`[brandfetch.ts] Searching brands with query: ${query} at URL: ${url}`);

  try {
    const response = await axios.get<BrandSearchResult[]>(url); // Direct axios call to external API
    console.log(`[brandfetch.ts] Received ${response.data.length} results for query "${query}":`, response.data);
    return response.data;
  } catch (error: unknown) {
    console.error(`[brandfetch.ts] Failed to search brands for query "${query}":`, error);
    // Basic error handling
    if (axios.isAxiosError<BrandfetchApiError>(error)) {
      const axiosError = error as AxiosError<BrandfetchApiError>;
      if (axiosError.response) {
        // Brandfetch might return 404 for no results, treat as empty array rather than error
        if (axiosError.response.status === 404) {
          console.log(`[brandfetch.ts] No brands found (404) for query: ${query}`);
          return []; // Return empty array for 404
        }
        console.error(`API Error searching brands (${axiosError.response.status}):`, axiosError.response.data?.message);
      } else {
        console.error('Network or other error during brand search:', axiosError.message);
      }
    } else if (error instanceof Error) {
      console.error('An unexpected error occurred during brand search:', error.message);
    } else {
      console.error('An unexpected non-error was thrown during brand search:', error);
    }
    return null; // Return null in case of errors (other than 404)
  }
}; 